import { loadRuntimeConfig } from './config';

export type SubscriptionPlanId = 'free' | 'essential' | 'studio' | 'enterprise' | 'trial';
export type BillingInterval = 'none' | 'monthly' | 'annual' | 'custom';
export interface SubscriptionLimits { storageBytes?: number; activeGalleries?: number; photosPerGallery: number }
export interface SubscriptionPlan {
  planId: SubscriptionPlanId;
  name: string;
  description: string;
  public: boolean;
  sortOrder: number;
  monthlyPriceCents?: number;
  annualPriceCents?: number;
  pricingMode: 'free' | 'fixed' | 'quote' | 'internal';
  limits: SubscriptionLimits;
  features?: { bookings?: boolean };
  minimumStorageBytes?: number;
}
export interface Subscription {
  photographerId: string;
  email: string;
  planId: SubscriptionPlanId;
  billingInterval: BillingInterval;
  customPriceCents?: number;
  customLimits?: Partial<SubscriptionLimits>;
  storageBytesUsed: number;
  activeGalleriesUsed: number;
  pendingChangeRequest?: { requestId: string; targetPlanId: Exclude<SubscriptionPlanId, 'trial'>; billingInterval: BillingInterval; note?: string; requestedAt: string };
  createdAt: string;
  updatedAt: string;
}
export interface SubscriptionSummary { subscription: Subscription; plan: SubscriptionPlan; effectiveLimits: SubscriptionLimits }

export interface UpdateSubscriptionInput {
  planId: SubscriptionPlanId;
  billingInterval: BillingInterval;
  customPriceCents?: number;
  customLimits?: Partial<SubscriptionLimits>;
  resolveRequestId?: string;
}

export interface ClientSummary {
  clientId: string;
  email: string;
  displayName: string;
  slug?: string;
  enabled: boolean;
  suspended: boolean;
  userStatus?: string;
  signedUpAt?: string;
  firstAppSeenAt?: string;
  lastSeenAt?: string;
  galleriesActive: number;
  galleriesTotal: number;
  photosTotal: number;
  videosTotal?: number;
  mediaTotal?: number;
  mediaBytes: number;
  watermarkAssetBytes: number;
  watermarkDerivativeBytes: number;
  exportRequests: number;
  subscription?: SubscriptionSummary;
  bookings: { entitled: boolean; enabled: boolean; total: number; pending: number; accepted: number; closed: number };
  health?: { highStorage: boolean; needsAttention: boolean; neverActive: boolean; noGalleries: boolean; noMedia: boolean; inactiveStatus: boolean };
}

export type ClientView = 'all' | 'attention' | 'never-active' | 'no-galleries' | 'no-media' | 'stale-14' | 'stale-30' | 'stale-90' | 'inactive-status' | 'high-storage';
export type ClientSort = 'activity' | 'name' | 'storage' | 'galleries' | 'downloads';
export type ClientSearchField = 'email' | 'name';
export interface ClientDashboardMetrics {
  clients: number;
  activeGalleries: number;
  photos: number;
  videos: number;
  media: number;
  bytes: number;
  health: { attention: number; neverActive: number; stale: number; inactiveStatus: number };
  generatedAt: string;
  ready: boolean;
}

export interface ClientListResponse {
  clients: ClientSummary[];
  nextCursor?: string;
  metrics: ClientDashboardMetrics;
  metricsUpdatedAt: string;
}

export interface InviteClientInput extends UpdateSubscriptionInput {
  email: string;
  displayName: string;
}

export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly details: Record<string, unknown>) {
    super(message);
  }
}

export interface Gallery {
  galleryId: string;
  title: string;
  gallerySlug: string;
  photographerSlug: string;
  isArchived: boolean;
  photoCount: number;
  imageCount?: number;
  videoCount?: number;
  watermarkStatus?: 'PROCESSING' | 'ACTIVE' | 'FAILED';
  watermarkReadyCount?: number;
  watermarkTargetCount?: number;
  watermarkFailureMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExportJob {
  exportId: string;
  galleryId: string;
  requestedBy: 'photographer' | 'public';
  quality?: 'web' | 'original';
  status: 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED';
  parts: Array<{ sizeBytes: number; zipKey?: string }>;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unitAmountCents: number;
  totalCents: number;
}

export interface Invoice {
  invoiceId: string;
  invoiceNumber: string;
  clientId: string;
  clientEmail: string;
  clientName: string;
  status: 'draft' | 'sent';
  currency: string;
  issueDate: string;
  dueDate: string;
  lineItems: InvoiceLineItem[];
  subtotalCents: number;
  totalCents: number;
  pdfStorageKey: string;
  createdAt: string;
  updatedAt: string;
  sentAt?: string;
}

export interface ClientDetail extends ClientSummary {
  galleries: Gallery[];
  exports: ExportJob[];
  invoices: Invoice[];
  exportZipBytes: number;
}

export type AccountDeletionStatus = 'PENDING' | 'PROCESSING' | 'FAILED' | 'COMPLETED';
export type AccountDeletionPhase = 'QUEUED' | 'GALLERIES' | 'MEDIA' | 'BOOKINGS' | 'ACCOUNT_RECORDS' | 'COGNITO' | 'COMPLETED';
export interface AccountDeletion {
  deletionId?: string;
  status: AccountDeletionStatus;
  phase: AccountDeletionPhase;
  updatedAt?: string;
  errorMessage?: string;
}

export interface PlatformSettings {
  maxUploadBytes: number;
  maxPhotosPerGallery: number;
  maxVideoUploadBytes: number;
  maxVideoDurationSeconds: number;
  maxVideosPerGallery: number;
  maxWatermarkRendersPerGalleryPerDay: number;
  signedMediaUrlTtlSeconds: number;
  exportZipRetentionDays: number;
  invoiceCurrency: string;
  invoicePaymentTerms: string;
  invoiceSenderName: string;
  invoiceSenderEmail: string;
  invoiceSenderAddress: string;
  galleryShareEmailSubject: string;
  galleryShareEmailHtml: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface CreateInvoiceInput {
  clientId: string;
  dueDate?: string;
  currency?: string;
  lineItems: Array<{
    description: string;
    quantity: number;
    unitAmountCents: number;
  }>;
}

export async function apiRequest<T>(
  path: string,
  options: {
    method?: string;
    token?: string | null;
    body?: unknown;
  } = {}
): Promise<T> {
  const config = await loadRuntimeConfig();
  const headers = new Headers();
  if (options.body !== undefined) {
    headers.set('content-type', 'application/json');
  }
  if (options.token) {
    headers.set('authorization', `Bearer ${options.token}`);
  }
  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new ApiError(payload.message ?? 'Request failed', response.status, payload);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

export function listClients(token: string, options: {
  limit?: 25 | 50 | 100;
  cursor?: string;
  searchField?: ClientSearchField;
  query?: string;
  view?: ClientView;
  sort?: ClientSort;
} = {}) {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', String(options.limit));
  if (options.cursor) params.set('cursor', options.cursor);
  if (options.searchField) params.set('searchField', options.searchField);
  if (options.query) params.set('q', options.query);
  if (options.view) params.set('view', options.view);
  if (options.sort) params.set('sort', options.sort);
  const query = params.size ? `?${params}` : '';
  return apiRequest<ClientListResponse>(`/admin/clients${query}`, { token });
}

export function inviteClient(token: string, input: InviteClientInput) {
  return apiRequest<{ clientId: string; invitation: { status: 'sent'; expiresInDays: number } }>('/admin/clients', { method: 'POST', token, body: input });
}

export function resendClientInvitation(token: string, clientId: string) {
  return apiRequest<{ clientId: string; invitation: { status: 'sent'; expiresInDays: number } }>(`/admin/clients/${clientId}/invitation/resend`, { method: 'POST', token });
}

export function getClient(token: string, clientId: string) {
  return apiRequest<{ client: ClientDetail }>(`/admin/clients/${clientId}`, { token });
}

export function suspendClient(token: string, clientId: string) {
  return apiRequest<{ client: ClientDetail }>(`/admin/clients/${clientId}/suspend`, { method: 'POST', token });
}

export function enableClient(token: string, clientId: string) {
  return apiRequest<{ client: ClientDetail }>(`/admin/clients/${clientId}/enable`, { method: 'POST', token });
}

export function disableClientGalleries(token: string, clientId: string) {
  return apiRequest<{ disabledCount: number; client: ClientDetail }>(`/admin/clients/${clientId}/disable-galleries`, { method: 'POST', token });
}

export function deleteClient(token: string, clientId: string, confirmation: string) {
  return apiRequest<{ deletion: AccountDeletion }>(`/admin/clients/${clientId}/delete`, {
    method: 'POST',
    token,
    body: { confirmation }
  });
}

export function getClientDeletion(token: string, clientId: string) {
  return apiRequest<{ deletion: AccountDeletion }>(`/admin/clients/${clientId}/deletion`, { token });
}

export function listSubscriptionPlans(token: string) {
  return apiRequest<{ plans: SubscriptionPlan[] }>('/admin/subscription-plans', { token });
}

export function updateClientSubscription(
  token: string,
  clientId: string,
  input: UpdateSubscriptionInput
) {
  return apiRequest<{ subscription: Subscription; client: ClientDetail }>(`/admin/clients/${clientId}/subscription`, { method: 'PUT', token, body: input });
}

export function declineSubscriptionRequest(token: string, clientId: string, requestId: string, reason?: string) {
  return apiRequest<{ subscription: Subscription; client: ClientDetail }>(`/admin/clients/${clientId}/subscription-change-request/decline`, {
    method: 'POST', token, body: { requestId, reason }
  });
}

export function getSettings(token: string) {
  return apiRequest<{ settings: PlatformSettings }>('/admin/settings', { token });
}

export function updateSettings(token: string, settings: PlatformSettings) {
  return apiRequest<{ settings: PlatformSettings }>('/admin/settings', { method: 'PUT', token, body: settings });
}

export function listInvoices(token: string, clientId?: string) {
  const query = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  return apiRequest<{ invoices: Invoice[] }>(`/admin/invoices${query}`, { token });
}

export function createInvoice(token: string, input: CreateInvoiceInput) {
  return apiRequest<{ invoice: Invoice }>('/admin/invoices', { method: 'POST', token, body: input });
}

export function sendInvoice(token: string, invoiceId: string) {
  return apiRequest<{ invoice: Invoice }>(`/admin/invoices/${invoiceId}/send`, { method: 'POST', token });
}
